// 保存単語（SRS）。PC版 src/vocab.js と同じデータ形・マージ規則に揃え、R2 経由で双方向同期する。
//   レコード: { key(小文字), id, word, base, senses[], context, source, createdAt, srs(FSRS Card),
//              reviewLog[], lastReviewedAt? }   ※key はローカル一意化用。アップロード時に外す。
//   tombstones は kv['vocabTombstones'] = [{ word(小文字), deletedAt }]。
import { db, kvGet, kvSet } from './db.js'
import { createEmptyCard, fsrs, Rating, State } from 'ts-fsrs'

const scheduler = fsrs()
const GRADES = [Rating.Again, Rating.Hard, Rating.Good, Rating.Easy]
const TOMBSTONE_TTL_MS = 90 * 24 * 60 * 60 * 1000

function newId() {
  // Math.random はブラウザでは使用可（Workflow スクリプトのみ禁止）。
  return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 7)
}

function vocabKey(w) {
  return (w && w.word ? String(w.word) : '').trim().toLowerCase()
}

// 保存済み srs（Date が構造化複製で Date のまま or ISO 文字列）を FSRS の Card へ。
function reviveCard(srs) {
  if (!srs) return createEmptyCard()
  return {
    ...srs,
    due: srs.due ? new Date(srs.due) : new Date(),
    last_review: srs.last_review ? new Date(srs.last_review) : undefined,
  }
}

// --- tombstones ---
async function getTombs() {
  return (await kvGet('vocabTombstones')) || []
}
async function setTombs(tombs) {
  await kvSet('vocabTombstones', tombs)
}
function upsertTomb(tombs, key, atIso) {
  if (!key) return tombs || []
  const at = atIso || new Date().toISOString()
  const out = (tombs || []).filter((t) => t && t.word !== key)
  out.push({ word: key, deletedAt: at })
  return out
}

// 単語を追加。既存（大小無視）なら dup。明示追加はその語の墓標を破棄（復活）。
export async function addWord({ word, base, senses, context, source }) {
  const w = String(word || '').trim()
  if (!w) return { ok: false }
  const key = w.toLowerCase()
  const d = await db()
  if (await d.get('vocab', key)) return { ok: true, dup: true }
  const rec = {
    key,
    id: newId(),
    word: w,
    base: base || '',
    senses: Array.isArray(senses) ? senses.slice(0, 8) : [],
    context: context || '',
    source: source && source.itemId ? source : null,
    createdAt: new Date().toISOString(),
    srs: createEmptyCard(),
    reviewLog: [],
  }
  await d.put('vocab', rec)
  await setTombs((await getTombs()).filter((t) => t && t.word !== key))
  return { ok: true, dup: false, id: rec.id }
}

export async function removeWord(key) {
  const d = await db()
  const rec = await d.get('vocab', key)
  await d.delete('vocab', key)
  if (rec) await setTombs(upsertTomb(await getTombs(), vocabKey(rec)))
  return { ok: true }
}

export async function vocabCount() {
  return (await db()).count('vocab')
}

// その語が既に保存済みか（大小無視）。辞書ポップアップの「保存/保存済み」表示に使う。
export async function isSaved(word) {
  const key = String(word || '').trim().toLowerCase()
  if (!key) return false
  return !!(await (await db()).get('vocab', key))
}

export async function listVocab() {
  const all = await (await db()).getAll('vocab')
  return all.sort((a, b) => (a.createdAt || '') < (b.createdAt || '') ? 1 : -1)
}

// 単語一覧ページ用：各語に SRS の状態（新規/期限切れ/次回期限）を添えて返す（作成日時の降順）。
export async function listVocabStatus(nowIso) {
  const now = nowIso ? new Date(nowIso) : new Date()
  const all = await listVocab()
  return all.map((w) => {
    const card = reviveCard(w.srs)
    return {
      ...w,
      isNew: !w.srs || card.state === State.New,
      dueIso: card.due.toISOString(),
      overdue: card.due <= now,
    }
  })
}

// ===== SRS =====
// 期限切れ/新規カードを due 昇順で。各カードに4段階の次回予定(preview)を付ける。
export async function dueCards(nowIso) {
  const now = nowIso ? new Date(nowIso) : new Date()
  const out = []
  for (const w of await listVocab()) {
    const card = reviveCard(w.srs)
    if (card.due > now) continue
    const rep = scheduler.repeat(card, now)
    out.push({
      ...w,
      isNew: !w.srs || card.state === State.New,
      preview: {
        again: rep[Rating.Again].card.due.toISOString(),
        hard: rep[Rating.Hard].card.due.toISOString(),
        good: rep[Rating.Good].card.due.toISOString(),
        easy: rep[Rating.Easy].card.due.toISOString(),
      },
    })
  }
  out.sort((a, b) => reviveCard(a.srs).due - reviveCard(b.srs).due)
  return out
}

// まだ期限前のうち最も早い次回期限（ISO）。無ければ null。
export async function nextDue(nowIso) {
  const now = nowIso ? new Date(nowIso) : new Date()
  let min = null
  for (const w of await listVocab()) {
    const dcard = reviveCard(w.srs).due
    if (dcard > now && (min === null || dcard < min)) min = dcard
  }
  return min ? min.toISOString() : null
}

// 採点。rating: 1=Again 2=Hard 3=Good 4=Easy。
export async function reviewCard(key, rating) {
  const r = Number(rating)
  if (!GRADES.includes(r)) return { ok: false }
  const d = await db()
  const w = await d.get('vocab', key)
  if (!w) return { ok: false }
  const now = new Date()
  const res = scheduler.next(reviveCard(w.srs), now, r)
  w.srs = res.card
  w.lastReviewedAt = now.toISOString()
  if (!Array.isArray(w.reviewLog)) w.reviewLog = []
  w.reviewLog.push({ rating: r, at: now.toISOString() })
  await d.put('vocab', w)
  return { ok: true, due: res.card.due.toISOString() }
}

// ===== クラウド同期 =====
// アップロード用ドキュメント（key を外し、PC版と同じ words/tombstones 形）。
export async function listDoc() {
  const words = (await (await db()).getAll('vocab')).map(({ key, ...rest }) => rest)
  return { words, tombstones: await getTombs() }
}

function mergeOne(local, remote) {
  const ta = local.lastReviewedAt || local.createdAt || ''
  const tb = remote.lastReviewedAt || remote.createdAt || ''
  const primary = tb > ta ? remote : local
  const other = primary === local ? remote : local
  const logMap = new Map()
  for (const e of [...(local.reviewLog || []), ...(remote.reviewLog || [])]) {
    if (e && e.at) logMap.set(e.at, e)
  }
  const reviewLog = [...logMap.values()].sort((x, y) => (x.at < y.at ? -1 : 1))
  const createdAt =
    local.createdAt && remote.createdAt
      ? local.createdAt < remote.createdAt
        ? local.createdAt
        : remote.createdAt
      : local.createdAt || remote.createdAt
  return {
    ...other,
    ...primary,
    id: local.id,
    senses: primary.senses && primary.senses.length ? primary.senses : other.senses || [],
    context: primary.context || other.context || '',
    source: primary.source || other.source || null,
    createdAt,
    srs: primary.srs || other.srs,
    lastReviewedAt: primary.lastReviewedAt || other.lastReviewedAt,
    reviewLog,
  }
}

// remote = { words, tombstones }（配列 or {words} も許容）をローカルへマージし保存。
// PC版 mergeRemote と同一ロジック（audio/clip フラグは PWA に無いので扱わない）。
export async function mergeRemoteDoc(remote) {
  const d = await db()
  const localWords = await (await db()).getAll('vocab')
  const localTombs = await getTombs()
  const remoteWords = Array.isArray(remote) ? remote : (remote && remote.words) || []
  const remoteTombs = (remote && !Array.isArray(remote) && remote.tombstones) || []

  const tombMap = new Map()
  for (const t of [...localTombs, ...remoteTombs]) {
    if (!t || !t.word) continue
    const ex = tombMap.get(t.word)
    if (!ex || (t.deletedAt || '') > (ex.deletedAt || '')) tombMap.set(t.word, t)
  }

  const byKey = new Map()
  for (const w of localWords) byKey.set(vocabKey(w), { ...w })
  let added = 0
  let merged = 0
  for (const r of remoteWords) {
    const k = vocabKey(r)
    if (!k) continue
    const l = byKey.get(k)
    if (!l) {
      byKey.set(k, { ...r, key: k, id: r.id || newId() })
      added++
    } else {
      byKey.set(k, { ...mergeOne(l, r), key: k })
      merged++
    }
  }

  for (const [k, w] of [...byKey]) {
    const t = tombMap.get(k)
    if (!t) continue
    const wt = w.lastReviewedAt || w.createdAt || ''
    if ((t.deletedAt || '') >= wt) byKey.delete(k)
    else tombMap.delete(k)
  }

  const cutoff = Date.now() - TOMBSTONE_TTL_MS
  const tombstones = [...tombMap.values()].filter((t) => {
    const ts = Date.parse(t.deletedAt || '')
    return Number.isNaN(ts) || ts >= cutoff
  })

  // ローカルストアを置き換え。
  const tx = d.transaction('vocab', 'readwrite')
  await tx.store.clear()
  for (const w of byKey.values()) await tx.store.put(w)
  await tx.done
  await setTombs(tombstones)
  return { added, merged, total: byKey.size, deleted: tombstones.length }
}
